'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@/utils';
import { getSocket, type UserValidationSocketResponse } from '@/utils/exato';

type UseKycSocketParams = {
  cpf?: string;
  onValidationSuccess?: () => void;
  onValidationError?: () => void;
  onValidationTimeout?: () => void;
};

type UseKycSocketReturn = {
  isConnected: boolean;
  startValidation: () => void;
  stopValidation: () => void;
};

const KYC_TIMEOUT_MS = 5 * 60 * 1000;
const KYC_SOCKET_LOG_PREFIX = '[useKycSocket]';

const maskCpf = (cpf?: string): string | undefined => {
  if (!cpf) return undefined;

  const digits = cpf.replace(/\D/g, '');

  if (digits.length < 5) {
    return '***';
  }

  return `${digits.slice(0, 3)}.***.***-${digits.slice(-2)}`;
};

const getValidationEventNameForLog = (cpf?: string): string | undefined => {
  const maskedCpf = maskCpf(cpf);

  return maskedCpf ? `user.validate:${maskedCpf}` : undefined;
};

const getErrorLogMeta = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
    };
  }

  return { error };
};

const getStartIgnoredReason = ({
  hasSocket,
  hasCpf,
  isListening,
}: {
  hasSocket: boolean;
  hasCpf: boolean;
  isListening: boolean;
}): string => {
  if (!hasSocket) return 'no-socket';
  if (!hasCpf) return 'no-cpf';
  if (isListening) return 'already-listening';

  return 'unknown';
};

export const useKycSocket = ({
  cpf,
  onValidationSuccess,
  onValidationError,
  onValidationTimeout,
}: UseKycSocketParams): UseKycSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef(getSocket());
  const isListeningRef = useRef(false);
  const validationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleConnect = useCallback(() => {
    logger.debug(
      `${KYC_SOCKET_LOG_PREFIX} socket connected cpf=${maskCpf(cpf) ?? 'unknown'} socketId=${
        socketRef.current?.id ?? 'unknown'
      }`,
    );
    setIsConnected(true);
  }, [cpf]);

  const handleDisconnect = useCallback(
    (reason?: string) => {
      logger.debug(
        `${KYC_SOCKET_LOG_PREFIX} socket disconnected reason=${reason ?? 'unknown'} cpf=${
          maskCpf(cpf) ?? 'unknown'
        }`,
      );
      setIsConnected(false);
    },
    [cpf],
  );

  const handleConnectError = useCallback(
    (error: unknown) => {
      logger.error(`${KYC_SOCKET_LOG_PREFIX} socket connect error`, {
        cpf: maskCpf(cpf),
        eventName: getValidationEventNameForLog(cpf),
        isListening: isListeningRef.current,
        ...getErrorLogMeta(error),
      });
    },
    [cpf],
  );

  const handleSocketError = useCallback(
    (error: unknown) => {
      logger.error(`${KYC_SOCKET_LOG_PREFIX} socket error`, {
        cpf: maskCpf(cpf),
        eventName: getValidationEventNameForLog(cpf),
        isListening: isListeningRef.current,
        ...getErrorLogMeta(error),
      });
    },
    [cpf],
  );

  const stopValidation = useCallback(() => {
    const socket = socketRef.current;

    if (!socket || !cpf || !isListeningRef.current) {
      return;
    }

    isListeningRef.current = false;

    const eventName = `user.validate:${cpf}`;
    const eventNameForLog = getValidationEventNameForLog(cpf);

    logger.debug(
      `${KYC_SOCKET_LOG_PREFIX} stopping validation eventName=${eventNameForLog} connected=${socket.connected}`,
    );

    socket.off(eventName);
    socket.emit('user.validation.stop', { cpf });

    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }
  }, [cpf]);

  const startValidation = useCallback(() => {
    const socket = socketRef.current;

    if (!socket || !cpf || isListeningRef.current) {
      logger.warn(
        `${KYC_SOCKET_LOG_PREFIX} start ignored reason=${getStartIgnoredReason({
          hasSocket: Boolean(socket),
          hasCpf: Boolean(cpf),
          isListening: isListeningRef.current,
        })} cpf=${maskCpf(cpf) ?? 'unknown'} connected=${socket?.connected ?? 'unknown'}`,
      );
      return;
    }

    isListeningRef.current = true;

    const eventName = `user.validate:${cpf}`;
    const eventNameForLog = getValidationEventNameForLog(cpf);

    logger.debug(
      `${KYC_SOCKET_LOG_PREFIX} starting validation eventName=${eventNameForLog} connected=${socket.connected}`,
    );

    socket.on(eventName, (data: UserValidationSocketResponse) => {
      logger.debug(
        `${KYC_SOCKET_LOG_PREFIX} validation event received eventName=${eventNameForLog} validated=${data.validated} userId=${data.userId}`,
      );

      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
        validationTimerRef.current = null;
      }

      isListeningRef.current = false;
      socket.off(eventName);

      if (data.validated === true) {
        onValidationSuccess?.();
        return;
      }

      logger.warn(
        `${KYC_SOCKET_LOG_PREFIX} validation failed eventName=${eventNameForLog} validated=${data.validated} userId=${data.userId}`,
      );
      onValidationError?.();
    });

    socket.emit('user.validation.start', { cpf });
    if (!socket.connected) {
      logger.debug(
        `${KYC_SOCKET_LOG_PREFIX} connecting socket eventName=${eventNameForLog}`,
      );
      socket.connect();
    }

    validationTimerRef.current = setTimeout(() => {
      isListeningRef.current = false;
      socket.off(eventName);

      try {
        logger.warn(
          `${KYC_SOCKET_LOG_PREFIX} validation timed out eventName=${eventNameForLog} timeoutMs=${KYC_TIMEOUT_MS}`,
        );
        socket.emit('user.validation.stop', { cpf });
      } finally {
        validationTimerRef.current = null;
        onValidationTimeout?.();
      }
    }, KYC_TIMEOUT_MS);
  }, [cpf, onValidationError, onValidationSuccess, onValidationTimeout]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) {
      logger.warn(
        `${KYC_SOCKET_LOG_PREFIX} socket unavailable cpf=${maskCpf(cpf) ?? 'unknown'}`,
      );
      return;
    }

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('error', handleSocketError);

    setIsConnected(socket.connected);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('error', handleSocketError);
    };
  }, [cpf, handleConnect, handleConnectError, handleDisconnect, handleSocketError]);

  useEffect(() => {
    return () => {
      stopValidation();
    };
  }, [stopValidation]);

  return {
    isConnected,
    startValidation,
    stopValidation,
  };
};
